import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithGoogle, resendVerificationEmail, logOut, syncUserProfile, resetPassword, completePasswordReset } from '../services/auth';
import { auth } from '../services/firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, applyActionCode, updateProfile } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const { user, loading } = useAuth();
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectPath = queryParams.get('redirect');
  const initialMode = queryParams.get('mode') === 'signup' ? 'signup' : 'signin';
  const from = redirectPath || (location.state as any)?.from?.pathname || "/dashboard";

  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot-password' | 'reset-password'>(initialMode);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendCooldown]);
  
  const isEmailUnverified = user && user.providerData.some(p => p.providerId === 'password') && !user.emailVerified;

  useEffect(() => {
    const m = queryParams.get('mode');
    const code = queryParams.get('oobCode');

    if (m === 'resetPassword' && code) {
      setMode('reset-password');
      setOobCode(code);
    } else if (m === 'verifyEmail' && code) {
      // Firebase email-verification action handler. The link from Firebase
      // lands here as /login?mode=verifyEmail&oobCode=... We apply the code,
      // refresh the current user (if any), then clean the URL so a refresh
      // doesn't re-trigger applyActionCode on an already-consumed code.
      let cancelled = false;
      (async () => {
        try {
          await applyActionCode(auth, code);
          if (cancelled) return;
          // Refresh the currently signed-in user's token so emailVerified flips.
          if (auth.currentUser) {
            try {
              await auth.currentUser.reload();
              await auth.currentUser.getIdToken(true);
            } catch (reloadErr) {
              console.warn('Post-verify reload failed:', reloadErr);
            }
          }
          if (cancelled) return;
          // Strip oobCode/mode from the URL so React Router and a manual
          // refresh don't re-attempt the now-consumed code.
          window.history.replaceState({}, '', '/login');
          setError('');
          if (auth.currentUser?.emailVerified) {
            setSuccessMsg('Email verified successfully. Redirecting…');
            navigate('/dashboard', { replace: true });
          } else {
            setSuccessMsg('Email verified successfully. Please sign in to continue.');
          }
        } catch (err: any) {
          if (cancelled) return;
          console.error('applyActionCode error:', err);
          window.history.replaceState({}, '', '/login');
          if (
            err?.code === 'auth/expired-action-code' ||
            err?.code === 'auth/invalid-action-code' ||
            err?.code === 'auth/user-disabled' ||
            err?.code === 'auth/user-not-found'
          ) {
            setError('This verification link has expired or was already used. Please sign in and request a new verification email.');
          } else {
            setError(err?.message || 'Could not verify this link. Please sign in and request a new verification email.');
          }
        }
      })();
      return () => { cancelled = true; };
    } else if (m === 'signup' || m === 'signin') {
      setMode(m);
    }
  }, [location.search]);

  useEffect(() => {
    if (!loading && user && !isEmailUnverified) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from, isEmailUnverified]);

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setSuccessMsg('');
      setIsActionLoading(true);
      await signInWithGoogle();
      // Redirection is handled by the useEffect above
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in with Google. Please try again.');
      setIsActionLoading(false); // Explicitly clear if catch triggers
    } finally {
      // We keep loading true if successful to wait for redirect useEffect
    }
  };

  const getFriendlyErrorMessage = (errorCode: string, defaultMsg: string) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'This email is already in use. Please sign in instead.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters.';
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Incorrect email or password.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/popup-closed-by-user':
        return 'Sign in cancelled.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      default:
        return defaultMsg;
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setIsActionLoading(true);

    const actionCodeSettings = {
      url: `${window.location.origin}/auth/verified`,
      handleCodeInApp: false
    };

    try {
      if (mode === 'signup') {
        // Client-side validation BEFORE creating the Firebase user so we don't
        // leave a half-provisioned account around if validation fails.
        const trimmedName = fullName.trim();
        if (!trimmedName) {
          setError('Please enter your full name.');
          setIsActionLoading(false);
          return;
        }
        if (password.length < 6) {
          setError('Password must be at least 6 characters.');
          setIsActionLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          setError('Passwords do not match.');
          setIsActionLoading(false);
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // Set displayName on the Auth profile FIRST so syncUserProfile picks
        // it up when it reads user.displayName for the Firestore write.
        try {
          await updateProfile(userCredential.user, { displayName: trimmedName });
        } catch (profErr) {
          console.warn('updateProfile failed (non-fatal):', profErr);
        }
        await syncUserProfile(userCredential.user);

        await sendEmailVerification(userCredential.user, actionCodeSettings);
        setSuccessMsg('Verification email sent. Please check your inbox and spam folder.');
        // Because they are not verified, isEmailUnverified becomes true and the view changes automatically
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await syncUserProfile(userCredential.user);
        
        // Force refresh token after login
        if (auth.currentUser) {
          await auth.currentUser.reload();
          await auth.currentUser.getIdToken(true);
        }
        // Redirection handled by useEffect if verified. If unverified, the view will change.
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(getFriendlyErrorMessage(err.code, err.message || `Failed to ${mode === 'signup' ? 'sign up' : 'sign in'}. Please try again.`));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setIsActionLoading(true);

    try {
      await resetPassword(email);
      setSuccessMsg('Password reset link sent. Please check your email.');
      // Automatically return to sign in after 3 seconds
      setTimeout(() => {
        setMode('signin');
        setSuccessMsg('');
      }, 5000);
    } catch (err: any) {
      console.error('Reset error:', err);
      // For security, don't reveal if account exists, but we can handle actual errors like invalid email format
      if (err.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later.');
      } else {
        // Show success anyway for security, or a generic "check your email"
        setSuccessMsg('If an account exists for this email, a reset link has been sent.');
        setTimeout(() => {
          setMode('signin');
          setSuccessMsg('');
        }, 5000);
      }
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!user || resendCooldown > 0 || isActionLoading) return;
    setError('');
    setSuccessMsg('');
    setIsActionLoading(true);

    const actionCodeSettings = {
      url: `${window.location.origin}/auth/verified`,
      handleCodeInApp: false
    };

    try {
      await sendEmailVerification(user, actionCodeSettings);
      setSuccessMsg('Verification email sent. Please check your inbox and spam folder.');
      setResendCooldown(60);
    } catch (err: any) {
      console.error('Resend error:', err);
      if (err.code === 'auth/too-many-requests') {
        setError('Too many verification attempts detected. Please wait a few minutes before trying again.');
        setResendCooldown(60);
      } else {
        setError(err.message || 'Failed to resend verification email.');
      }
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsActionLoading(true);
    try {
      await logOut();
      setError('');
      setSuccessMsg('');
      navigate('/login', { replace: true });
    } catch (err: any) {
      console.error('Logout error:', err);
      setError('Failed to sign out.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleVerifiedRefresh = async () => {
    if (!user) return;
    setIsActionLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        const freshUser = auth.currentUser;
        await freshUser.getIdToken(true);
        
        if (import.meta.env.DEV) {
          console.log('After reload, emailVerified:', freshUser.emailVerified);
        }

        if (freshUser.emailVerified) {
          navigate(from, { replace: true });
        } else {
          await logOut();
          setError('Verification completed? Please sign in again to refresh your session.');
        }
      }
    } catch (err: any) {
      console.error('Verify error:', err);
      setError(err.message || 'Failed to check verification status.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleResetPasswordComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setIsActionLoading(true);

    try {
      await completePasswordReset(oobCode, password);
      setSuccessMsg('Password updated successfully. You can now sign in.');
      setTimeout(() => {
        setMode('signin');
        setSuccessMsg('');
        setPassword('');
        setConfirmPassword('');
      }, 5000);
    } catch (err: any) {
      console.error('Complete reset error:', err);
      if (err.code === 'auth/expired-action-code' || err.code === 'auth/invalid-action-code') {
        setError('This password reset link is invalid or expired. Please request a new one.');
      } else {
        setError(err.message || 'Failed to update password. Please try again.');
      }
    } finally {
      setIsActionLoading(false);
    }
  };

  const toggleMode = (newMode: 'signin' | 'signup' | 'forgot-password' | 'reset-password') => {
    setMode(newMode);
    setError('');
    setSuccessMsg('');
  };

  if (loading) return null;

  return (
    <div className="app">
      <Header />
      <main className={styles.loginPage}>
        <div className={styles.loginCard}>
          {isEmailUnverified ? (
            <>
              <h1>Email Verification Required</h1>
              <p>Please verify your email before accessing the platform.</p>

              <p style={{ fontSize: '14px', color: '#ccc', marginBottom: '16px' }}>
                Verification email usually arrives within 1 minute. Please check your spam/junk folder.
              </p>

              {error && <div className={styles.error}>{error}</div>}
              {successMsg && <div className={styles.success} style={{ color: 'var(--accent)', marginBottom: '16px', background: 'rgba(57, 255, 20, 0.1)', padding: '12px', borderRadius: '8px', border: '1px solid var(--accent)' }}>{successMsg}</div>}
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px' }}>
                <button 
                  className={styles.submitBtn} 
                  onClick={handleVerifiedRefresh}
                  disabled={isActionLoading}
                >
                  I've verified my email
                </button>
                <button 
                  className={styles.googleBtn} 
                  onClick={handleResendVerification}
                  disabled={isActionLoading || resendCooldown > 0}
                  style={{ justifyContent: 'center' }}
                >
                  {resendCooldown > 0 ? `Resend verification email (${resendCooldown}s)` : 'Resend verification email'}
                </button>
                <button
                  className={styles.googleBtn}
                  onClick={handleLogout}
                  disabled={isActionLoading}
                  style={{
                    justifyContent: 'center',
                    background: 'transparent',
                    color: '#ffffff',
                    border: '1px solid rgba(255,255,255,0.4)',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontWeight: 500,
                    transition: 'background 0.15s ease, border-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)';
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.7)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.4)';
                  }}
                >
                  Sign out
                </button>
              </div>
            </>
          ) : mode === 'reset-password' ? (
            <>
              <h1>Set New Password</h1>
              <p>Please enter and confirm your new password.</p>

              {error && <div className={styles.error}>{error}</div>}
              {successMsg && <div className={styles.success} style={{ color: 'var(--accent)', marginBottom: '16px', background: 'rgba(57, 255, 20, 0.1)', padding: '12px', borderRadius: '8px', border: '1px solid var(--accent)' }}>{successMsg}</div>}

              <form className={styles.emailForm} onSubmit={handleResetPasswordComplete}>
                <input 
                  type="password" 
                  placeholder="New Password" 
                  required 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isActionLoading}
                />
                <input 
                  type="password" 
                  placeholder="Confirm New Password" 
                  required 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isActionLoading}
                />
                <button type="submit" className={styles.submitBtn} disabled={isActionLoading}>
                  {isActionLoading ? 'Updating...' : 'Update Password'}
                </button>
              </form>

              <p className={styles.footerText}>
                <button 
                  type="button" 
                  onClick={() => toggleMode('signin')} 
                  style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: '#888', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                >
                  Back to Sign In
                </button>
              </p>
            </>
          ) : mode === 'forgot-password' ? (
            <>
              <h1>Reset Password</h1>
              <p>Enter your email to receive a password reset link.</p>

              {error && <div className={styles.error}>{error}</div>}
              {successMsg && <div className={styles.success} style={{ color: 'var(--accent)', marginBottom: '16px', background: 'rgba(57, 255, 20, 0.1)', padding: '12px', borderRadius: '8px', border: '1px solid var(--accent)' }}>{successMsg}</div>}

              <form className={styles.emailForm} onSubmit={handleForgotPasswordSubmit}>
                <input 
                  type="email" 
                  placeholder="Email address" 
                  required 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isActionLoading}
                />
                <button type="submit" className={styles.submitBtn} disabled={isActionLoading}>
                  {isActionLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <p className={styles.footerText}>
                <button 
                  type="button" 
                  onClick={() => toggleMode('signin')} 
                  style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: '#888', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                >
                  Back to Sign In
                </button>
              </p>
            </>
          ) : (
            <>
              <h1>{mode === 'signup' ? 'Create your PCCheckr account' : 'Sign in to start your diagnostic report'}</h1>
              
              {error && <div className={styles.error}>{error}</div>}
              {successMsg && <div className={styles.success} style={{ color: 'var(--accent)', marginBottom: '16px', background: 'rgba(57, 255, 20, 0.1)', padding: '12px', borderRadius: '8px', border: '1px solid var(--accent)' }}>{successMsg}</div>}
              
              <button className={styles.googleBtn} onClick={handleGoogleSignIn} disabled={isActionLoading}>
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
                {isActionLoading ? 'Please wait...' : 'Continue with Google'}
              </button>
              
              <div className={styles.divider}>
                <span>or</span>
              </div>
              
              <form className={styles.emailForm} onSubmit={handleEmailSubmit}>
                {mode === 'signup' && (
                  <input
                    type="text"
                    placeholder="Full name"
                    required
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={isActionLoading}
                  />
                )}
                <input
                  type="email"
                  placeholder="Email address"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isActionLoading}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="password"
                    placeholder={mode === 'signup' ? 'Password (min 6 characters)' : 'Password'}
                    required
                    minLength={mode === 'signup' ? 6 : undefined}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isActionLoading}
                  />
                  {mode === 'signin' && (
                    <button
                      type="button"
                      className={styles.forgotPasswordLink}
                      onClick={() => toggleMode('forgot-password')}
                    >
                      Forgot Password?
                    </button>
                  )}
                </div>
                {mode === 'signup' && (
                  <input
                    type="password"
                    placeholder="Confirm password"
                    required
                    minLength={6}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isActionLoading}
                  />
                )}
                <button type="submit" className={styles.submitBtn} disabled={isActionLoading}>
                  {isActionLoading ? 'Please wait...' : (mode === 'signup' ? 'Create Account' : 'Sign In')}
                </button>
              </form>
              
              <p className={styles.footerText}>
                {mode === 'signup' ? (
                  <>
                    Already have an account?{' '}
                    <button 
                      type="button" 
                      onClick={() => toggleMode('signin')} 
                      style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                    >
                      Sign in
                    </button>
                  </>
                ) : (
                  <>
                    Don't have an account?{' '}
                    <button 
                      type="button" 
                      onClick={() => toggleMode('signup')} 
                      style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                    >
                      Sign up
                    </button>
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
